
import React from 'react';
import type { ProductVariant } from '@/types/product';

interface SizeSelectorProps {
  variants: ProductVariant[];
  selectedVariant: ProductVariant;
  onVariantChange: (variant: ProductVariant) => void;
}

// Ordre standard des tailles (utilisé comme fallback si les positions ne sont pas définies)
const standardSizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

// Fonction pour normaliser une taille (convertir "XXL" en "2XL" si nécessaire)
const normalizeSize = (size: string): string => {
  if (size === 'XXL') return '2XL';
  if (size === 'XXXL') return '3XL';
  return size;
};

// Fonction pour obtenir l'index d'une taille dans l'ordre standard
const getStandardSizeIndex = (size: string): number => {
  // Normaliser la taille d'abord
  const normalizedSize = normalizeSize(size);
  return standardSizeOrder.indexOf(normalizedSize);
};

const SizeSelector: React.FC<SizeSelectorProps> = ({
  variants,
  selectedVariant,
  onVariantChange,
}) => {
  // Extraire les tailles uniques des variantes
  const uniqueSizes = React.useMemo(() => {
    // Créer un Map pour stocker les tailles uniques avec leur position
    const sizeMap = new Map<string, number>();
    
    // Collecter toutes les tailles uniques avec leur position
    variants.forEach(v => {
      if (v.size && !sizeMap.has(v.size)) {
        sizeMap.set(v.size, v.position || 0);
      }
    });
    
    // Convertir le Map en tableau de tailles
    const sizes = Array.from(sizeMap.keys());
    
    // Trier les tailles:
    // 1. D'abord par position si elle est définie dans la base de données
    // 2. Sinon, utiliser l'ordre standard des tailles
    sizes.sort((a, b) => {
      const posA = sizeMap.get(a) || 0;
      const posB = sizeMap.get(b) || 0;
      
      // Utiliser les positions de la BD dès qu'elles sont différentes
      if (posA !== posB) {
        return posA - posB;
      }
      
      // Si les positions sont identiques ou non définies, utiliser l'ordre standard
      const indexA = getStandardSizeIndex(a);
      const indexB = getStandardSizeIndex(b);
      
      // Si les deux tailles sont dans notre ordre standard
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      
      // Si une seule taille est dans notre ordre standard
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      // Pour les tailles numériques pures (ex: "42", "44")
      if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
        return parseInt(a, 10) - parseInt(b, 10);
      }
      
      // Fallback: ordre alphabétique
      return a.localeCompare(b);
    });
    
    console.log("SizeSelector: Tailles uniques trouvées dans l'ordre:", sizes);
    return sizes;
  }, [variants]);
  
  // Fonction pour trouver une variante avec la taille sélectionnée
  const findVariantWithSize = (size: string) => {
    // Si ce produit a des couleurs, maintenir la couleur sélectionnée si possible
    if (selectedVariant.colorName) {
      // Chercher une variante avec la même couleur mais la nouvelle taille
      const sameColorNewSize = variants.find(v => 
        v.size === size && v.colorName === selectedVariant.colorName
      );
      
      if (sameColorNewSize) {
        return sameColorNewSize;
      }
    }
    
    // Sinon, prendre la première variante avec cette taille
    return variants.find(v => v.size === size) || selectedVariant;
  };

  const handleSizeChange = (size: string) => {
    const newVariant = findVariantWithSize(size);
    console.log("SizeSelector: Changement de taille", {
      previousVariant: `${selectedVariant.size} - ${selectedVariant.colorName}`,
      newVariant: `${newVariant.size} - ${newVariant.colorName}`
    });
    onVariantChange(newVariant);
  };

  if (uniqueSizes.length <= 1) return null;

  // Détecter si c'est une carte cadeau (montants en dollars: "25$", "50$", etc.)
  const isGiftCard = uniqueSizes.every(size => /^\d+\$?$/.test(size));
  const label = isGiftCard ? "Montant" : "Taille";

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="flex flex-wrap gap-2">
        {uniqueSizes.map(size => (
          <button
            key={size}
            onClick={() => handleSizeChange(size)}
            className={`
              min-w-[3rem] h-10 px-3 flex items-center justify-center 
              text-sm font-medium rounded-md transition-colors
              ${selectedVariant.size === size
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }
            `}
            aria-pressed={selectedVariant.size === size}
          >
            {size}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SizeSelector;
